import { common_utils } from "./common_utils";
import { Constants } from "./constants";
import {
	CommandPermissions,
	GuildSettings,
	GuildSettingsProps,
	GuildEntityColumns,
	SeshTextChannelData,
	NativeEventData,
	Mentionable,
	AttendeeRoleDataWithIDs,
	NativeEventLocationType,
} from "./sesh_types";

const safe_mappings_guild_entity_to_guild_settings: Partial<
	Record<keyof GuildEntityColumns, keyof GuildSettings>
> = {
	custom_timezone: "timezone",
	secondary_timezones: "secondary_timezones",
	default_signup_options: "signup_options",
	event_channel_override_only_admins: "event_channel_override_only_admins",
	event_channel_require_send_messages: "event_channel_require_send_messages",
	poll_channel_override_only_admins: "poll_channel_override_only_admins",
	poll_channel_require_send_messages: "poll_channel_require_send_messages",
	auto_remove_event_messages: "clean_event_messages",
	auto_remove_user_commands: "clean_user_commands",
	can_add_poll_options: "poll_add",
	is_default_poll_type_single_vote: "is_default_poll_type_single_vote",
	use_24h_clock: "use_24h_clock",
	custom_prefix: "prefix",
	use_channel_calendars_mode: "use_channel_calendars_mode",
	use_discord_timestamps: "use_discord_timestamps",
	is_ai_enabled: "is_ai_enabled",
	display_attendees_as_plain_text: "display_attendees_as_plain_text",
	default_hide_attendees: "default_hide_attendees",
	use_reactions_for_polls: "use_reactions_for_polls",
	default_event_thread_settings: "default_event_thread_settings",
	event_channel_forum_tag_ids: "event_channel_forum_tag_ids",
	poll_channel_forum_tag_ids: "poll_channel_forum_tag_ids",
	default_event_embed_color: "default_event_embed_color",
	default_poll_embed_color: "default_poll_embed_color",
	default_event_server_reminders: "default_event_server_reminders",
	default_role_mentions_on_event_create:
		"default_role_mentions_on_event_create",
	default_user_mentions_on_event_create:
		"default_user_mentions_on_event_create",
};

const safe_mappings_guild_settings_to_guild_entity: Partial<
	Record<keyof GuildSettings, keyof GuildEntityColumns>
> = {
	timezone: "custom_timezone",
	secondary_timezones: "secondary_timezones",
	signup_options: "default_signup_options",
	event_channel_override_only_admins: "event_channel_override_only_admins",
	event_channel_require_send_messages: "event_channel_require_send_messages",
	poll_channel_override_only_admins: "poll_channel_override_only_admins",
	poll_channel_require_send_messages: "poll_channel_require_send_messages",
	clean_event_messages: "auto_remove_event_messages",
	clean_user_commands: "auto_remove_user_commands",
	poll_add: "can_add_poll_options",
	is_default_poll_type_single_vote: "is_default_poll_type_single_vote",
	use_24h_clock: "use_24h_clock",
	is_ai_enabled: "is_ai_enabled",
	prefix: "custom_prefix",
	use_channel_calendars_mode: "use_channel_calendars_mode",
	use_discord_timestamps: "use_discord_timestamps",
	display_attendees_as_plain_text: "display_attendees_as_plain_text",
	default_hide_attendees: "default_hide_attendees",
	use_reactions_for_polls: "use_reactions_for_polls",
	default_event_thread_settings: "default_event_thread_settings",
	event_channel_forum_tag_ids: "event_channel_forum_tag_ids",
	poll_channel_forum_tag_ids: "poll_channel_forum_tag_ids",
	default_event_embed_color: "default_event_embed_color",
	default_poll_embed_color: "default_poll_embed_color",
	default_event_server_reminders: "default_event_server_reminders",
	default_role_mentions_on_event_create:
		"default_role_mentions_on_event_create",
	default_user_mentions_on_event_create:
		"default_user_mentions_on_event_create",
};

const unsafe_mappings_guild_settings_to_guild_entity: Partial<
	Record<keyof GuildSettings, Array<keyof GuildEntityColumns>>
> = {
	attendee_role_data: [
		"default_attendee_role_ids",
		"remove_roles_on_event_end",
		"role_on_rsvp_add_time",
	],
	default_native_event_data: [
		"default_native_event_location_type",
		"default_native_event_location_value",
	],
	event_channel: ["events_channel_id"],
	poll_channel: ["polls_channel_id"],
	permissions: ["command_permissions"],
};

/**
does not handle the following
	- command_permissions
	- events_channel_id
	- polls_channel_id
	- attendee_role_data
	- native_event_data
*/
export function getSettingForChannel<
	T extends keyof GuildSettings,
	Key extends keyof GuildEntityColumns
>(
	guild_settings: Pick<GuildSettings, T>,
	individual_channel_settings_overrides: GuildEntityColumns,
	key: Key
): GuildEntityColumns[Key] {
	const has_override =
		individual_channel_settings_overrides &&
		individual_channel_settings_overrides.hasOwnProperty(key);

	if (has_override) {
		return individual_channel_settings_overrides[key];
	}

	return guild_settings[
		safe_mappings_guild_entity_to_guild_settings[<string>key]
	];
}

export function guildSettingsStyle_hasActiveChannelOverrideForSetting(
	individual_channel_settings_overrides: GuildEntityColumns,
	key: keyof GuildSettings
) {
	const safe_guild_entity_style_key =
		convertGuildSettingsKeyToGuildEntityStyle(key);

	if (safe_guild_entity_style_key) {
		return hasActiveChannelOverrideForSetting(
			individual_channel_settings_overrides,
			safe_guild_entity_style_key
		);
	}

	const unsafe_guild_entity_style_keys =
		unsafe_mappings_guild_settings_to_guild_entity[key];

	if (unsafe_guild_entity_style_keys) {
		for (const key of unsafe_guild_entity_style_keys) {
			const has_override = hasActiveChannelOverrideForSetting(
				individual_channel_settings_overrides,
				key
			);
			if (has_override) {
				return true;
			}
		}
	}

	return false;
}

export function hasActiveChannelOverrideForSetting(
	individual_channel_settings_overrides: GuildEntityColumns,
	key: keyof GuildEntityColumns
): boolean {
	const has_override = Boolean(
		individual_channel_settings_overrides &&
			individual_channel_settings_overrides.hasOwnProperty(key)
	);

	return has_override;
}

export function convertGuildSettingsKeyToGuildEntityStyle<
	GuildSettingsKey extends keyof GuildSettings
>(key: GuildSettingsKey): keyof GuildEntityColumns {
	return <keyof GuildEntityColumns>(
		safe_mappings_guild_settings_to_guild_entity[<string>key]
	);
}

export function guildSettingsStyle_setIndividualChannelSettingOverride<
	GuildSettingsKey extends keyof GuildSettings
>(
	individual_channel_settings_overrides: GuildEntityColumns,
	key: GuildSettingsKey,
	value: GuildSettings[GuildSettingsKey],
	settings_props: GuildSettingsProps
) {
	const guild_entity_style_key: string =
		convertGuildSettingsKeyToGuildEntityStyle(key);

	if (guild_entity_style_key) {
		individual_channel_settings_overrides[guild_entity_style_key] = value;
	} else if (key === "event_channel") {
		const value_channel = <SeshTextChannelData>value;
		individual_channel_settings_overrides.events_channel_id =
			value_channel?.id;
	} else if (key === "poll_channel") {
		const value_channel = <SeshTextChannelData>value;
		individual_channel_settings_overrides.polls_channel_id =
			value_channel?.id;
	} else if (key === "permissions") {
		individual_channel_settings_overrides.command_permissions =
			common_utils.formatCommandPermissionsForDB(
				<CommandPermissions[]>value,
				settings_props.guild_data.id
			);
	} else if (key === "attendee_role_data") {
		const attendee_role_data_from_guild_settings = <
			AttendeeRoleDataWithIDs
		>value;

		individual_channel_settings_overrides.remove_roles_on_event_end =
			attendee_role_data_from_guild_settings?.remove_on_end ?? null;
		individual_channel_settings_overrides.role_on_rsvp_add_time =
			attendee_role_data_from_guild_settings?.role_add_time ?? null;
		individual_channel_settings_overrides.default_attendee_role_ids =
			attendee_role_data_from_guild_settings?.role_ids ?? null;
	} else if (key === "default_native_event_data") {
		const default_native_event_data_from_guild_settings =
			value as NativeEventData;

		individual_channel_settings_overrides.default_native_event_location_type =
			default_native_event_data_from_guild_settings?.location?.type ??
			null;
		individual_channel_settings_overrides.default_native_event_location_value =
			default_native_event_data_from_guild_settings?.location?.value ??
			null;
		individual_channel_settings_overrides.default_native_event_custom_description =
			default_native_event_data_from_guild_settings?.custom_description ??
			null;
		individual_channel_settings_overrides.default_native_event_image_url =
			default_native_event_data_from_guild_settings?.image_url ?? null;
	}
}

export function guildSettingsStyle_convertAttendeeRoles(
	individual_channel_settings_overrides: GuildEntityColumns
) {
	const default_attendee_role_ids =
		individual_channel_settings_overrides.default_attendee_role_ids ?? [];
	const role_on_rsvp_add_time =
		individual_channel_settings_overrides.role_on_rsvp_add_time ??
		Constants.DEFAULT_ADD_ROLE_ON_RSVP_ADD_TIME;
	const remove_roles_on_event_end =
		individual_channel_settings_overrides.remove_roles_on_event_end ??
		Constants.DEFAULT_REMOVE_ROLE_ON_EVENT_END;

	return {
		remove_on_end: remove_roles_on_event_end,
		role_add_time: role_on_rsvp_add_time,
		role_ids: default_attendee_role_ids,
	} as AttendeeRoleDataWithIDs;
}

/**
should only be used for the following, just use get_setting_for_channel() (with GuildEntity style key instead) otherwise
	- command_permissions
	- events_channel_id
	- polls_channel_id
	- attendee_role_data
*/
export function guildSettingsStyle_getIndividualChannelSettingOverride<
	GuildSettingsKeys extends keyof GuildSettings,
	Key extends GuildSettingsKeys
>(
	guild_settings: Pick<GuildSettings, GuildSettingsKeys>,
	individual_channel_settings_overrides: GuildEntityColumns,
	key: Key,
	settings_props: {
		all_text_channels: SeshTextChannelData[];
		roles: Mentionable[];
		guild_data: { id: string };
	}
): GuildSettings[Key] {
	if (
		!guildSettingsStyle_hasActiveChannelOverrideForSetting(
			individual_channel_settings_overrides,
			key
		)
	) {
		return guild_settings[key];
	}

	const guild_entity_style_key =
		convertGuildSettingsKeyToGuildEntityStyle(key);

	if (guild_entity_style_key) {
		return <GuildSettings[Key]>(
			getSettingForChannel(
				guild_settings,
				individual_channel_settings_overrides,
				guild_entity_style_key
			)
		);
	} else if (key === "event_channel") {
		const events_channel_id =
			individual_channel_settings_overrides.events_channel_id;

		return <GuildSettings[Key]>(
			settings_props.all_text_channels.find(
				(mentionable) => events_channel_id === mentionable.id
			)
		);
	} else if (key === "poll_channel") {
		const polls_channel_id =
			individual_channel_settings_overrides.polls_channel_id;

		return <GuildSettings[Key]>(
			settings_props.all_text_channels.find(
				(mentionable) => polls_channel_id === mentionable.id
			)
		);
	} else if (key === "permissions") {
		return <GuildSettings[Key]>(
			common_utils.formatCommandPermissionsForWeb(
				individual_channel_settings_overrides.command_permissions,
				settings_props.guild_data.id
			)
		);
	} else if (key === "attendee_role_data") {
		return <GuildSettings[Key]>(
			guildSettingsStyle_convertAttendeeRoles(
				individual_channel_settings_overrides
			)
		);
	} else if (key === "default_native_event_data") {
		if (
			!individual_channel_settings_overrides.default_native_event_location_type
		) {
			return null as unknown as GuildSettings[Key];
		}

		const result: NativeEventData = {
			location: {
				type: individual_channel_settings_overrides.default_native_event_location_type as NativeEventLocationType,
				value:
					individual_channel_settings_overrides.default_native_event_location_value ??
					undefined,
			},
			custom_description:
				individual_channel_settings_overrides.default_native_event_custom_description ??
				undefined,
			image_url:
				individual_channel_settings_overrides.default_native_event_image_url ??
				undefined,
		};

		return <GuildSettings[Key]>result;
	}

	throw new Error("Invalid key");
}
